import { z } from "zod";

import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";

export const fileSchema = z.object({
  attachmentId: z.number().optional(), // ID after successful upload
  errorMessage: z.string().optional(), // Error message if upload fails
  file: z.instanceof(File),
  id: z.string(), // Unique identifier for the file in the form/upload state
  isSourceFile: z.boolean(),
  key: z.string().optional(), // S3 key for the file
  name: z
    .string()
    .min(1, "File name cannot be blank. Please input a name.")
    .max(150, "File name cannot be longer than 150 characters."),
  originalName: z.string(),
  previousVersionId: z.number().nullable(),
  status: z.enum(["uploading", "uploaded", "error"]),
  totalParts: z.number().optional(), // Total number of parts
  uploadedParts: z.number().optional(), // Number of parts uploaded
  uploadId: z.string().optional(), // S3 multipart upload ID
  uploadProgress: z.number().min(0).max(100).optional(), // Overall progress percentage
});

export const uploadFilesFormSchema = z
  .object({
    files: z.array(fileSchema),
  })
  .superRefine((data, ctx) => {
    // NEW RULE: Validate that files with the same extension have unique names
    // Files with different extensions CAN have the same name
    const filesByExtension: Map<string, { index: number; name: string }[]> =
      new Map();

    // Group files by extension
    for (const [index, file] of data.files.entries()) {
      const extension = getExtensionFromFilename(file.originalName);
      const baseName = file.name;

      if (!filesByExtension.has(extension)) {
        filesByExtension.set(extension, []);
      }

      filesByExtension.get(extension)!.push({ index, name: baseName });
    }

    // Check for duplicates within each extension group
    for (const [extension, files] of filesByExtension) {
      const nameIndexMap: Map<string, number> = new Map();
      const duplicateIndices: Set<number> = new Set();

      for (const { index, name } of files) {
        if (nameIndexMap.has(name)) {
          duplicateIndices.add(index);
          // Add the first occurrence index as well if it's not already marked
          if (!duplicateIndices.has(nameIndexMap.get(name)!)) {
            duplicateIndices.add(nameIndexMap.get(name)!);
          }
        } else {
          nameIndexMap.set(name, index);
        }
      }

      // Add validation errors for duplicates within the same extension
      for (const index of duplicateIndices) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Files with the same extension (.${extension}) must have unique names`,
          path: ["files", index, "name"],
        });
      }
    }
  });

export type FileFormData = z.infer<typeof fileSchema>;
export type UploadFilesFormData = z.infer<typeof uploadFilesFormSchema>;
